<template>
    <div class="workforce-plan-rows" role="rowgroup">
        <div
            v-if="topSpacerHeight"
            class="workforce-plan-virtual-spacer"
            :style="{height: `${topSpacerHeight}px`}"
            aria-hidden="true"
        ></div>
        <employee-workforce-plan-row
            v-for="(employee, employeeIndex) in virtualEmployees"
            :key="rowKey(employeeIndex)"
            :api="api"
            :employee="employee"
            :inspected="inspectedEmployeeId === Number(employee.employee_id)"
            :focused="focusedEmployeeId === Number(employee.employee_id)"
            :row-number="range.start + employeeIndex + 1"
            :row-has-selection="selectedEmployeeIds.has(Number(employee.employee_id))"
            :row-selected="selectedFullRowEmployeeIds.has(Number(employee.employee_id))"
            :weeks="weeks"
            :current-status-week="currentStatusWeek"
        ></employee-workforce-plan-row>
        <div
            v-if="bottomSpacerHeight"
            class="workforce-plan-virtual-spacer"
            :style="{height: `${bottomSpacerHeight}px`}"
            aria-hidden="true"
        ></div>
    </div>
</template>

<script>
import planUtils from '@/utils/employeeWorkforcePlan';
import EmployeeWorkforcePlanRow from './EmployeeWorkforcePlanRow.vue';

const ROW_HEIGHT = 30;
const MOBILE_ROW_HEIGHT = 38;
const MIN_OVERSCAN = 5;
const MAX_OVERSCAN = 15;
const OVERSCAN_VIEWPORT_RATIO = 0.5;
const INITIAL_RENDER_COUNT = 32;

export default {
    name: 'EmployeeWorkforcePlanRows',
    components: {EmployeeWorkforcePlanRow},
    props: {
        api: {type: Object, required: true},
        currentStatusWeek: {type: Object, default: null},
        employees: {type: Array, required: true},
        inspectedEmployeeId: {type: Number, default: null},
        focusedEmployeeId: {type: Number, default: null},
        mobile: {type: Boolean, default: false},
        selectedEmployeeIds: {type: Set, required: true},
        selectedFullRowEmployeeIds: {type: Set, required: true},
        weeks: {type: Array, required: true}
    },
    data() {
        return {
            range: {start: 0, end: INITIAL_RENDER_COUNT},
            poolSize: INITIAL_RENDER_COUNT
        };
    },
    computed: {
        rowHeight() {
            return this.mobile ? MOBILE_ROW_HEIGHT : ROW_HEIGHT;
        },
        virtualEmployees() {
            const total = this.employees.length;
            const start = Math.max(0, Math.min(Number(this.range.start) || 0, total));
            const end = Math.max(
                start,
                Math.min(Number(this.range.end) || 0, total)
            );
            return this.employees.slice(start, end);
        },
        topSpacerHeight() {
            return Math.max(0, Number(this.range.start) || 0) * this.rowHeight;
        },
        bottomSpacerHeight() {
            const hidden = Math.max(
                0,
                this.employees.length - (Number(this.range.end) || 0)
            );
            return hidden * this.rowHeight;
        }
    },
    watch: {
        employees() {
            this.resetRange(false);
        },
        mobile() {
            this.scheduleRangeUpdate();
        }
    },
    beforeDestroy() {
        if (this._rangeFrame) window.cancelAnimationFrame(this._rangeFrame);
        this._rangeFrame = null;
    },
    methods: {
        handleViewportScroll(viewport, contentOffset) {
            this._viewport = viewport || this._viewport;
            this._contentOffset = Number(contentOffset) || 0;
            const scrollTop = this._viewport
                ? Number(this._viewport.scrollTop) || 0
                : 0;
            if (scrollTop === this._lastScrollTop) return;
            this._lastScrollTop = scrollTop;
            this.scheduleRangeUpdate();
        },
        scheduleRangeUpdate(viewport, contentOffset) {
            if (viewport) this._viewport = viewport;
            if (contentOffset !== undefined) {
                this._contentOffset = Number(contentOffset) || 0;
            }
            if (
                typeof window === 'undefined'
                || typeof window.requestAnimationFrame !== 'function'
            ) {
                this.updateRange();
                return;
            }
            if (this._rangeFrame) return;
            this._rangeFrame = window.requestAnimationFrame(() => {
                this._rangeFrame = null;
                this.updateRange();
            });
        },
        updateRange(viewport, contentOffset) {
            if (viewport) this._viewport = viewport;
            if (contentOffset !== undefined) {
                this._contentOffset = Number(contentOffset) || 0;
            }
            const total = this.employees.length;
            const target = this._viewport;
            const fallbackEnd = Math.min(total, INITIAL_RENDER_COUNT);
            const visibleRowCount = target
                ? Math.max(1, Math.ceil(target.clientHeight / this.rowHeight))
                : fallbackEnd;
            const overscan = Math.min(
                MAX_OVERSCAN,
                Math.max(
                    MIN_OVERSCAN,
                    Math.ceil(visibleRowCount * OVERSCAN_VIEWPORT_RATIO)
                )
            );
            const windowSize = target
                ? Math.min(total, Math.max(
                    1,
                    visibleRowCount + 1 + overscan * 2
                ))
                : fallbackEnd;
            const nextRange = target
                ? planUtils.virtualRowRange({
                    total,
                    scrollTop: target.scrollTop,
                    viewportHeight: target.clientHeight,
                    contentOffset: Number(this._contentOffset) || 0,
                    rowHeight: this.rowHeight,
                    overscan,
                    windowSize
                })
                : {start: 0, end: fallbackEnd};
            if (this.poolSize !== windowSize) this.poolSize = windowSize;
            if (
                nextRange.start === this.range.start
                && nextRange.end === this.range.end
            ) return;
            this.range = nextRange;
        },
        resetRange(resetScroll) {
            if (resetScroll && this._viewport) this._viewport.scrollTop = 0;
            this._lastScrollTop = resetScroll ? 0 : null;
            this.range = {
                start: 0,
                end: Math.min(this.employees.length, INITIAL_RENDER_COUNT)
            };
            this.poolSize = Math.max(1, this.range.end - this.range.start);
            this.$nextTick(this.scheduleRangeUpdate);
        },
        rowKey(employeeIndex) {
            const poolSize = Math.max(1, Number(this.poolSize) || 1);
            const absoluteIndex = this.range.start + Number(employeeIndex || 0);
            return `workforce-plan-row-slot:${absoluteIndex % poolSize}`;
        }
    }
};
</script>

<style scoped>
.workforce-plan-rows {
    display: grid;
    width: 100%;
    grid-column: 1 / -1;
    grid-template-columns: var(--plan-columns);
}

.workforce-plan-virtual-spacer {
    width: 100%;
    grid-column: 1 / -1;
    pointer-events: none;
}
</style>
